// File: frontend/src/components/Profile/NotificationSettings.tsx
// Improved NotificationSettings component for user profile page

import React, { useState } from 'react';
import { Bell, BellOff, Smartphone, AlertCircle, CheckCircle, Settings, Info, HelpCircle } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';

interface NotificationSettingsProps {
  userRole?: 'admin' | 'provider' | 'customer';
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ userRole = 'provider' }) => {
  const {
    supported,
    permission,
    subscribed,
    loading,
    error,
    subscribe,
    unsubscribe,
    refreshToken
  } = usePushNotifications();

  const [showHelp, setShowHelp] = useState(false);

  // Handle subscription toggle
  const handleToggleNotifications = async () => {
    if (subscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  // Get status info for the current state
  const getStatusInfo = () => {
    if (!supported) {
      return {
        color: 'text-gray-500',
        bgColor: 'bg-gray-100',
        icon: AlertCircle,
        message: 'Push notifications are not supported on this browser',
        description: 'Your browser or device does not support push notifications.'
      };
    }

    if (permission === 'denied') {
      return {
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        icon: BellOff,
        message: 'Notifications are blocked',
        description: 'You have blocked notifications in your browser settings. Please enable them to receive instant alerts.'
      };
    }

    if (subscribed) {
      return {
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        icon: CheckCircle,
        message: 'Notifications are active',
        description: 'You will receive instant notifications for important updates and activities.'
      };
    }

    return {
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      icon: Bell,
      message: 'Notifications are disabled',
      description: 'Enable notifications to receive instant alerts about important activities.'
    };
  };

  // Get notification types based on user role
  const getNotificationTypes = () => {
    switch (userRole) {
      case 'admin':
        return [
          'New user registrations',
          'New provider applications',
          'System alerts and updates',
          'Payment confirmations',
          'Customer support requests'
        ];
      case 'provider':
        return [
          'New booking requests',
          'Booking status updates',
          'Customer messages',
          'Payment confirmations',
          'Review notifications'
        ];
      case 'customer':
        return [
          'Booking confirmations',
          'Service provider responses',
          'Service updates',
          'Payment receipts',
          'Promotional offers'
        ];
      default:
        return [
          'General notifications',
          'System updates',
          'Important announcements'
        ];
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;
  const notificationTypes = getNotificationTypes();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
        <div className="flex items-center space-x-3 mb-2 sm:mb-0">
          <div className={`p-2 rounded-lg ${statusInfo.bgColor}`}>
            <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
              Push Notifications
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Control how you receive instant notifications
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowHelp(!showHelp)}
          className="self-start p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
          title="Help & Information"
        >
          <HelpCircle className="h-5 w-5" />
        </button>
      </div>

      {/* Status Card */}
      <div className={`p-4 rounded-lg ${statusInfo.bgColor} mb-6`}>
        <div className="flex items-start space-x-3">
          <StatusIcon className={`h-5 w-5 ${statusInfo.color} mt-0.5 flex-shrink-0`} />
          <div>
            <p className={`font-medium ${statusInfo.color} mb-1`}>
              {statusInfo.message}
            </p>
            <p className={`text-sm ${statusInfo.color.replace('600', '700')}`}>
              {statusInfo.description}
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* What you'll receive */}
      {(subscribed || permission === 'granted') && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <Info className="h-4 w-4 mr-2 text-blue-500" />
            You'll receive notifications for:
          </h4>
          <div className="bg-blue-50 rounded-lg p-4">
            <ul className="text-sm text-blue-800 space-y-2">
              {notificationTypes.map((type, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  {type}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleToggleNotifications}
          disabled={loading || !supported || permission === 'denied'}
          className={`flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
            subscribed
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {loading ? (
            <span className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Processing...</span>
            </span>
          ) : subscribed ? (
            <span className="flex items-center justify-center space-x-2">
              <BellOff className="h-4 w-4" />
              <span>Disable Notifications</span>
            </span>
          ) : (
            <span className="flex items-center justify-center space-x-2">
              <Bell className="h-4 w-4" />
              <span>Enable Notifications</span>
            </span>
          )}
        </button>

        {subscribed && (
          <button
            onClick={refreshToken}
            disabled={loading}
            className="px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
            title="Refresh notification token"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Refresh Token</span>
          </button>
        )}
      </div>

      {/* Help Section */}
      {showHelp && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-4">
            Notification Information
          </h4>
          
          <div className="space-y-4">
            {/* Technical Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-medium text-gray-700">Browser Support:</span>
                <span className={`ml-2 ${supported ? 'text-green-600' : 'text-red-600'}`}>
                  {supported ? 'Supported' : 'Not Supported'}
                </span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-medium text-gray-700">Permission:</span>
                <span className={`ml-2 capitalize ${
                  permission === 'granted' ? 'text-green-600' : 
                  permission === 'denied' ? 'text-red-600' : 'text-orange-600'
                }`}>
                  {permission}
                </span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-medium text-gray-700">Status:</span>
                <span className={`ml-2 ${subscribed ? 'text-green-600' : 'text-gray-600'}`}>
                  {subscribed ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-medium text-gray-700">Device:</span>
                <span className="ml-2 text-gray-600 flex items-center">
                  <Smartphone className="h-3 w-3 mr-1" />
                  Web Browser
                </span>
              </div>
            </div>

            {/* Browser help for denied permissions */}
            {permission === 'denied' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h5 className="text-sm font-medium text-orange-900 mb-2">
                  How to enable notifications:
                </h5>
                <ol className="text-sm text-orange-800 space-y-1">
                  <li>1. Click the lock icon (🔒) or notification icon in your browser's address bar</li>
                  <li>2. Change notifications setting to "Allow"</li>
                  <li>3. Refresh this page and try enabling notifications again</li>
                </ol>
              </div>
            )}

            {/* Privacy information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="text-sm font-medium text-blue-900 mb-2">
                Privacy & Security:
              </h5>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Notifications are sent securely through your browser</li>
                <li>• You can disable notifications at any time</li>
                <li>• No personal data is stored on external servers</li>
                <li>• Notifications work even when the app is closed</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;